import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatPrice } from "@/lib/constants";
import FinanceReport from "@/components/FinanceReport";
import "./Dashboard.css";
import {
  Building2, Home, Tag, CheckCircle2, AlertTriangle, Bell, UserPlus,
  TrendingUp, Sparkles, CalendarClock, Send,
} from "lucide-react";

const Card = ({ title, value, icon: Icon, color, onClick, testid, subtitle }) => (
  <button
    type="button"
    onClick={onClick}
    className={`kpi-card dashboard-compact text-left w-full h-full min-h-[100px] ${onClick ? "clickable" : ""}`}
    data-testid={testid}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 text-xs font-semibold uppercase tracking-wider text-slate-500 break-words">{title}</div>
      {Icon && (
        <div className="dashboard-kpi-icon flex w-6 h-6 sm:w-8 sm:h-8 shrink-0 rounded-lg items-center justify-center" style={{ background: color + "22", color }} aria-hidden="true">
          <Icon className="w-4 h-4" />
        </div>
      )}
    </div>
    <div className="mt-2 text-2xl font-bold text-slate-900 font-display" data-testid={`${testid}-value`}>{value}</div>
    {subtitle && <div className="mt-1 text-xs text-slate-500">{subtitle}</div>}
  </button>
);

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboard/stats`, { params: { period } });
      setStats(data);
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const periods = [
    { v: "today", l: "Bugün" }, { v: "week", l: "Bu Hafta" },
    { v: "month", l: "Bu Ay" }, { v: "30d", l: "Son 30 Gün" },
    { v: "all", l: "Tüm Zamanlar" },
  ];

  return (
    <div className="dashboard-overview space-y-5" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-display">Genel Bakış</h1>
        </div>
        <div className="flex flex-wrap gap-1 p-1 bg-white border border-slate-200 rounded-lg w-full sm:w-fit">
          {periods.map((p) => (
            <button
              key={p.v} onClick={() => setPeriod(p.v)}
              className={`px-3 py-1.5 text-xs rounded-md whitespace-nowrap ${period === p.v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`period-${p.v}`}
            >{p.l}</button>
          ))}
        </div>
      </div>

      {loading || !stats ? (
        <div className="text-slate-500">Yükleniyor…</div>
      ) : (
        <>
          <section className="space-y-2">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Stok ve Satış</h2>
            </div>
            <div className="dashboard-stock-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 items-stretch">
              <Card title="Toplam Proje" value={stats.total_projects} icon={Building2} color="#0F172A"
                    onClick={() => nav("/projects")} testid="kpi-projects" />
              <Card title="Aktif Proje" value={stats.active_projects} icon={Sparkles} color="#F59E0B"
                    onClick={() => nav("/projects?status=active")} testid="kpi-active-projects" />
              <Card title="Satışta" value={stats.units?.["Satışta"] || 0} icon={Tag} color="#10B981"
                    onClick={() => nav("/units?status=Satışta")} testid="kpi-satista" />
              <Card title="Rezerve" value={stats.units?.["Rezerve"] || 0} icon={Bell} color="#3B82F6"
                    onClick={() => nav("/units?status=Rezerve")} testid="kpi-rezerve" />
              <Card title="Kapora Alınan" value={stats.units?.["Kapora Alındı"] || 0} icon={Home} color="#F59E0B"
                    onClick={() => nav("/units?status=Kapora%20Al%C4%B1nd%C4%B1")} testid="kpi-kapora" />
              <Card title="Satılan" value={stats.units?.["Satıldı"] || 0} icon={CheckCircle2} color="#334155"
                    onClick={() => nav("/units?status=Sat%C4%B1ld%C4%B1")} testid="kpi-satildi" />
              <Card title="Güncellemede" value={stats.units?.["Güncellemede"] || 0} icon={AlertTriangle} color="#8B5CF6"
                    onClick={() => nav("/units?status=Güncellemede")} testid="kpi-güncellemede" />
              <Card title="Süresi Dolan Rez." value={stats.expired_reservations} icon={AlertTriangle} color="#EF4444"
                    onClick={() => nav("/sales?status=expired")} testid="kpi-expired" />
            </div>
          </section>

          <section className="space-y-2">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Müşteri Takibi</h2>
            </div>
            <div className="dashboard-customer-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 items-stretch">
              <Card title="Geciken Takip" value={stats.overdue_followups || 0} icon={AlertTriangle} color="#EF4444"
                    onClick={() => nav("/customers?followup=overdue")} testid="kpi-overdue-followups" />
              <Card title="Bugünkü Takipler" value={stats.today_followups} icon={Bell} color="#F59E0B"
                    onClick={() => nav("/customers?followup=today")} testid="kpi-followups" />
              <Card title="Yaklaşan Takip" value={stats.upcoming_followups || 0} icon={CalendarClock} color="#3B82F6"
                    onClick={() => nav("/customers?followup=upcoming")} testid="kpi-upcoming-followups" />
              <Card title="Teklif Verilen" value={stats.offer_customers || 0} icon={Send} color="#8B5CF6"
                    onClick={() => nav("/customers?status=Teklif%20Verildi")} testid="kpi-offer-customers" />
              <Card title="Yeni Müşteri" value={stats.new_customers} icon={UserPlus} color="#10B981"
                    onClick={() => nav("/customers?status=Yeni")} testid="kpi-new-customers" />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600" data-testid="conversion-metrics">
              <span>İlgi → Rezervasyon: <b className="text-slate-900">{stats.interest_to_reservation || 0}/{stats.interest_pairs || 0}</b> (%{stats.interest_to_reservation_rate || 0})</span>
              <span>Teklif → Satış: <b className="text-slate-900">{stats.offer_to_sale || 0}/{stats.offer_pairs || 0}</b> (%{stats.offer_to_sale_rate || 0})</span>
              <span className="text-slate-400">Tüm kayıtlar</span>
            </div>
          </section>

          {user?.role !== "agent" && (
            <section className="space-y-2">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Finans ve Rezervasyon</h2>
              </div>
            <div className="dashboard-sales-grid grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
              <button type="button" onClick={() => nav(`/sales?tab=sales&status=Aktif&period=${period}`)}
                      className="kpi-card dashboard-compact clickable text-left w-full">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Toplam Aktif Satış</div>
                <div className="mt-2 flex items-center gap-3">
                  <TrendingUp className="w-7 h-7 text-emerald-500" />
                  <div>
                    <div className="text-2xl font-bold font-display" data-testid="total-sales-count">{stats.total_sales}</div>
                    <div className="text-sm text-slate-500">Ciro: <span className="font-semibold text-slate-900" data-testid="total-revenue">{formatPrice(stats.total_revenue_try, "TRY")}</span></div>
                  </div>
                </div>
              </button>
              <button type="button" onClick={() => nav("/sales?tab=reservations&status=expiring_today")}
                      className="kpi-card dashboard-compact clickable text-left w-full">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bugün Süresi Dolacak Rezervasyonlar</div>
                <div className="mt-2 text-2xl font-bold font-display" data-testid="expiring-today">{stats.expiring_today}</div>
              </button>
            </div>
            </section>
          )}
          <FinanceReport compact period={period} />
        </>
      )}
    </div>
  );
}
